import type {
  LegalAgreementAcceptPath,
  LegalAgreementEmailPath,
  LegalAgreementEmailRequest,
  LegalAgreementIndexQuery,
  LegalTermsShowPath
} from '~/services/api/generated'
import * as operations from '~/services/api/generated'
import { createGeneratedResourceOperation } from '~/services/api/createGeneratedResourceOperation'
import { pdfBlobSchema } from '~/utils/pdfBlobSchema'

const unwrapData =
  <Arguments extends readonly unknown[], Data>(
    operation: (...args: Arguments) => Promise<{ data: Data }>
  ) =>
  async (...args: Arguments): Promise<Data> =>
    (await operation(...args)).data

export function useLegalAgreementService() {
  const apiBaseUrl = useRuntimeConfig().public.apiBaseUrl
  const baseURL = `${apiBaseUrl}/api/legal-agreements`
  const client = useApiRequest()
  const agreementPath = (id: string) => `${baseURL}/${encodeURIComponent(id)}`

  return {
    /** The caller's legal agreements, filtered by the query. */
    index: unwrapData(
      createGeneratedResourceOperation(
        client,
        baseURL,
        operations.LegalAgreementIndexSuccessSchema,
        'GET',
        {
          channel: 'query',
          schema: operations.LegalAgreementIndexQuerySchema.strict(),
          select: (query: LegalAgreementIndexQuery) => query
        }
      )
    ),

    /** The current Terms & Conditions or Privacy Policy text; public. */
    showTerms: unwrapData(
      createGeneratedResourceOperation(
        client,
        {
          schema: operations.LegalTermsShowPathSchema,
          parse: (type: LegalTermsShowPath['type']) => ({ type }),
          buildUrl: (path) => `${apiBaseUrl}/api/legal-terms/${encodeURIComponent(path.type)}`
        },
        operations.LegalTermsShowSuccessSchema,
        'GET'
      )
    ),

    /** Email a legal agreement's PDF to the addresses. */
    emailLegalAgreement: createGeneratedResourceOperation(
      client,
      {
        schema: operations.LegalAgreementEmailPathSchema,
        parse: (
          id: LegalAgreementEmailPath['id'],
          _emails: LegalAgreementEmailRequest['emails']
        ) => ({ id }),
        buildUrl: (path) => `${agreementPath(path.id)}/emails`
      },
      operations.LegalAgreementEmailSuccessSchema,
      'POST',
      {
        channel: 'body',
        schema: operations.LegalAgreementEmailRequestSchema.strict(),
        select: (_id, emails) => ({ emails })
      }
    ),

    /** Accepts a pending legal agreement as the caller; returns the agreement as accepted. */
    accept: unwrapData(
      createGeneratedResourceOperation(
        client,
        {
          schema: operations.LegalAgreementAcceptPathSchema,
          parse: (id: LegalAgreementAcceptPath['id']) => ({ id }),
          buildUrl: (path) => `${agreementPath(path.id)}/acceptance`
        },
        operations.LegalAgreementAcceptSuccessSchema,
        'POST'
      )
    ),

    /** Download a legal agreement's PDF: the draft before acceptance, the signed copy after. */
    downloadLegalAgreement: async (legalAgreementId: string): Promise<Blob> => {
      const { id } = operations.LegalAgreementDocumentPathSchema.parse({ id: legalAgreementId })
      const response = await client<Blob, 'blob'>(`${baseURL}/${encodeURIComponent(id)}/document`, {
        method: 'GET',
        responseType: 'blob'
      })
      return pdfBlobSchema.parse(response)
    }
  }
}
